import { combineEpics, ofType } from 'redux-observable'
import { EMPTY, of, zip } from 'rxjs'
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators'

import { getTimezoneId, getTZOffset } from 'utils/dateUtils'
import { isSimulationAndTraining } from 'utils/plans'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualCompanyId,
  selectActualLicenses,
  selectHasSelectedCompanyFilterAccess,
} from 'containers/App/selectors'
import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'

import { DATE_FILTER_CHANGE, SCANBACK_CHANGE } from '../constants'
import { INVESTIGATION_FILTERS_BY_VALUE } from '../EmailOverview/constants'
import {
  selectDateFilter,
  selectDateRange,
  selectHasIncidentWidgetAccess,
  selectWithoutScanback,
} from '../selectors'

import { phishingWidgetRedux } from './redux'

const getPhishingWidgetEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      phishingWidgetRedux.INITIATE,
      DATE_FILTER_CHANGE,
      SCANBACK_CHANGE
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.dashboard.home)
      return true
    }),
    switchMap(() => {
      const state = state$.value
      // INITIATE and company-data success turn the loader on. Cancel so a child
      // the user cannot swap into does not leave the widget spinning.
      if (!selectHasSelectedCompanyFilterAccess(state)) {
        return of(phishingWidgetRedux.cancel())
      }
      const licenses = selectActualLicenses(state)
      const hasWidgetAccess = selectHasIncidentWidgetAccess(state)

      if (isSimulationAndTraining(licenses.plan) || !hasWidgetAccess) {
        return EMPTY
      }

      const selectedCompany = state.partnerOptions.selectedCompany?.value
      const feDateRange = selectDateRange(state)
      const dateFilter = selectDateFilter(state)
      const withoutScanback = selectWithoutScanback(state)
      const companyIds = selectActualCompanyId(state)

      const baseParams = {
        custom_dates: Array.isArray(dateFilter),
        pageSize: 10000,
        pageNumber: 1,
        emailDateRange: feDateRange,
        withoutScanback,
        company_ids: [companyIds],
        ...INVESTIGATION_FILTERS_BY_VALUE.PHISHING,
        has_incident: true,
        logDateRange: null,
        // TODO: we should handle this differently since those values can be changed
        classification: ['Unclassified', 'Phishing'],
      }

      const queryTypes = ['phish_data', 'user_count']

      const paramsForPhishData = {
        offset: getTZOffset(),
        timezone: getTimezoneId(),
      }

      return zip(
        ...queryTypes.map((queryType) =>
          api.dashboard.getPhishingWidget(selectedCompany, {
            ...baseParams,
            queryType,
            ...(queryType === 'phish_data' ? paramsForPhishData : {}),
          })
        )
      ).pipe(
        map((responses) => {
          const combinedResponse = responses.reduce(
            (acc, response) => ({ ...acc, ...response.response }),
            {}
          )
          return phishingWidgetRedux.onSuccess(combinedResponse)
        }),
        catchError((error) => of(epicsErrorHandler(phishingWidgetRedux.onError, error))),
        takeUntil(action$.pipe(ofType(phishingWidgetRedux.CANCEL, phishingWidgetRedux.ERROR)))
      )
    })
  )

export default combineEpics(getPhishingWidgetEpic)
